import { z } from 'zod';
import { OrgRole } from './auth.js';
import { CalendarDate, ProjectStatus } from './project.js';
/**
 * Danh bạ dự án của **một người** — APM-PSET-c.
 *
 * Nguồn cho màn hình "Danh sách dự án": mọi dự án người này đã tham gia, cộng những dự
 * án đang mời họ vào. Sidebar bên trái là một tập con của danh sách này.
 *
 * ## Vì sao một hàng, không phải hai kiểu khác nhau
 *
 * Bảng có cùng bộ cột cho cả bốn nhóm, nên dự án được mời cũng là một
 * `ProjectDirectoryRow` — chỉ khác `invitationId` khác `null`. Hai kiểu riêng
 * thì màn hình phải dựng hai bảng cạnh nhau và cột nào cũng phải khai hai lần.
 */
export const ProjectDirectoryRow = z.object({
    id: z.uuid(),
    key: z.string(),
    name: z.string(),
    /** `null` khi người tạo đã rời tổ chức. */
    createdByName: z.string().nullable(),
    status: ProjectStatus,
    startDate: CalendarDate.nullable(),
    endDate: CalendarDate.nullable(),
    /**
     * Số người và số việc — `null` với dự án **chỉ đang mời**.
     *
     * ⚠️ Cố ý không trả số cho lời mời. Người được mời chưa phải thành viên, và
     * "dự án này có 12 người, 340 việc" là dữ liệu của một dự án họ có thể sẽ từ
     * chối. Tên dự án thì lời mời vốn đã phải nói ra; quy mô thì không.
     */
    memberCount: z.number().int().nonnegative().nullable(),
    taskCount: z.number().int().nonnegative().nullable(),
    /** Khác `null` = đang mời, và đây là id để Nhận / Từ chối ngay tại dòng. */
    invitationId: z.uuid().nullable(),
    /** Đang bị ẩn khỏi sidebar. */
    hidden: z.boolean(),
    /**
     * Dự án bị từ chối khi duyệt — APM-PREJ. Chỉ **người tạo** nhận được dòng này.
     *
     * Cùng `status = archived` với dự án đã lưu trữ thật, nên không suy ra được từ
     * `status`: không có cờ này thì nó hiện ở nhóm "Hoàn thành" như một dự án đã xong.
     */
    rejected: z.boolean(),
    /** Lý do người duyệt ghi lúc từ chối; `null` khi không ghi hoặc không phải dự án bị từ chối. */
    rejectionNote: z.string().nullable(),
});
export const ProjectDirectory = z.object({
    rows: z.array(ProjectDirectoryRow),
    /**
     * `false` = người này chưa bao giờ sắp sidebar.
     *
     * Khác "đã sắp, không ẩn gì" dù hai thứ vẽ ra cùng một sidebar: màn hình cần
     * phân biệt để nói đúng trạng thái, và để không hiện "bạn đã ẩn 0 dự án".
     */
    curated: z.boolean(),
    /**
     * Thứ tự đã lưu, đã lọc bỏ id của dự án không còn tồn tại.
     *
     * Thanh bên xếp theo mảng này trước, rồi mới tới những dự án chưa có trong
     * nó — xem `orderRows`.
     */
    order: z.array(z.uuid()),
    /**
     * Thứ tự của **bảng** "Danh sách dự án" — một mảng riêng, không phải `order`.
     *
     * Hai thứ tự cho hai danh sách, và đó là chủ đích (yêu cầu người dùng
     * 2026-09-18): bảng là danh mục đầy đủ, thanh bên là mục truy cập nhanh. Kéo
     * một dòng trong bảng sắp lại **bảng**, không dời chỗ nó trên thanh bên.
     *
     * Dùng chung một mảng thì không thể có hai cách sắp, và mỗi cú kéo trong bảng
     * sẽ âm thầm đổi thanh bên — đúng thứ vừa bị báo là lỗi.
     */
    tableOrder: z.array(z.uuid()),
});
/**
 * Thân của `PATCH /me/sidebar-projects` — gửi **trọn bộ** danh sách ẩn.
 *
 * Không vá từng phần: màn hình "Danh sách dự án" là một bản nháp có nút Hoàn thành/Huỷ,
 * nên thứ gửi lên là kết quả cuối của cả lượt sửa. Gửi từng thao tác thì Huỷ
 * không còn nghĩa gì.
 */
export const UpdateSidebarProjectsInput = z.object({
    hidden: z.array(z.uuid()).max(500),
    /**
     * Thứ tự người dùng đã kéo thả. Dự án không có trong mảng này xếp **sau**,
     * theo thứ tự mặc định — nên một dự án mới vào không đẩy ai đi đâu.
     */
    order: z.array(z.uuid()).max(500),
    /**
     * Thứ tự của bảng — `.default([])` chứ không bắt buộc.
     *
     * Trường này thêm sau `hidden`/`order`, và một client chưa cập nhật gửi thân
     * cũ lên sẽ nhận 400 cho **cả** lượt lưu nếu nó bắt buộc: người dùng bấm Hoàn
     * thành và mất luôn danh sách ẩn vừa sắp, vì một trường họ không hề đụng tới.
     */
    tableOrder: z.array(z.uuid()).max(500).default([]),
});
/**
 * A project of mine in **another** organization — APM-PSPACE-j,
 * docs/plan-personal-space.md §3.7.
 *
 * A session is pinned to one organization, so the project list only ever shows
 * that one. This list is the way across: a guest sees, from their personal
 * space, the projects they were invited to in organization A — and from A, the
 * projects of their personal space to go back. Clicking one switches the
 * session's organization, then opens the project.
 *
 * Only what the switch needs plus a name to recognise it by: no counts, no
 * dates, no members — anything more is data of an organization the session is
 * not in.
 */
export const ElsewhereProject = z.object({
    id: z.uuid(),
    key: z.string(),
    name: z.string(),
    orgId: z.uuid(),
    orgName: z.string(),
    /** Personal space — shown by its i18n name instead of `orgName`. */
    orgIsPersonal: z.boolean(),
    /** My position in that organization — `GUEST` is labelled "Khách". */
    myOrgRole: OrgRole,
});
/** Wrapped in an object so "none" is a value, not an empty body. */
export const ElsewhereProjects = z.object({ projects: z.array(ElsewhereProject) });
/**
 * Where a task lives when it is in one of my projects of another organization —
 * for the old `/work?task=X` links (APM-PSPACE-j). `location: null` = not in a
 * project of mine elsewhere, or no such task; the caller cannot tell the two
 * apart, on purpose.
 */
export const ElsewhereTaskLocation = z.object({
    location: z.object({ orgId: z.uuid(), projectId: z.uuid(), taskId: z.uuid() }).nullable(),
});
/** Sáu nhóm của bảng "Tất cả dự án", theo đúng thứ tự hiện trên màn hình. */
export const PROJECT_DIRECTORY_GROUPS = [
    'invited',
    'notStarted',
    'running',
    'done',
    'archived',
    'rejected',
];
/**
 * Nhóm của một dòng — hàm thuần, dùng chung để bảng và test nói cùng một thứ.
 *
 * Sáu `ProjectStatus` gộp về bốn nhóm trạng thái, và mấy chỗ gộp cần nói rõ:
 *
 * - `pending_approval` vào **Chưa bắt đầu**: dự án chờ quản lý duyệt thì chưa
 *   ai làm gì trong đó cả.
 * - `on_hold` vào **Đang thực thi**: nó là một dự án đang chạy bị tạm dừng, chứ
 *   không phải một dự án chưa mở hay đã xong. Cột Trạng thái của chính dòng ấy
 *   vẫn ghi "Tạm dừng", nên không mất thông tin nào.
 * - `archived` đứng **nhóm riêng** "Đã lưu trữ" (người dùng yêu cầu 2026-10-08).
 *   Trước đó nó gộp vào Hoàn thành, và một dự án tạo nhầm hay bị huỷ nằm lẫn
 *   giữa những dự án đã làm xong thật — đọc bảng thì không phân biệt được.
 *   Riêng dự án **bị từ chối** (`rejected`, cũng `status = archived`) vẫn đứng
 *   nhóm của nó — nó chưa từng được mở (APM-PREJ).
 */
export function directoryGroupOf(row) {
    // Lời mời đứng riêng và đứng đầu: nó là việc đang chờ người này quyết.
    if (row.invitationId !== null)
        return 'invited';
    // Bị từ chối cũng `archived` — phải xét trước `status`, không thì lọt vào "Đã lưu trữ".
    if (row.rejected)
        return 'rejected';
    switch (row.status) {
        case 'pending_approval':
        case 'planning':
            return 'notStarted';
        case 'active':
        case 'on_hold':
            return 'running';
        case 'completed':
            return 'done';
        case 'archived':
            return 'archived';
    }
}
/**
 * Xếp các dòng theo thứ tự người dùng đã kéo — APM-PSET-c.
 *
 * Hàm thuần vì đây là chỗ dễ sai **im lặng**: danh sách vẫn vẽ đủ, chỉ là sai
 * thứ tự, và không có gì trên màn hình nói ra điều đó.
 *
 * Dự án **không** có trong `order` xếp sau, giữ nguyên thứ tự đầu vào. Đó là
 * đường đi của một dự án vừa được thêm: nó xuất hiện ở cuối chứ không chen vào
 * giữa danh sách người dùng vừa sắp.
 */
export function orderRows(rows, order) {
    const rank = new Map(order.map((id, index) => [id, index]));
    // `?? Infinity` cho dự án chưa xếp; `stable sort` của JS giữ nguyên thứ tự
    // tương đối giữa chúng, nên phần chưa xếp không đảo lộn mỗi lần vẽ lại.
    return [...rows].sort((left, right) => (rank.get(left.id) ?? Infinity) - (rank.get(right.id) ?? Infinity));
}
//# sourceMappingURL=project-directory.js.map